import { Head, Link, router, usePage } from "@inertiajs/react"
import { Search, Volume2, ArrowRight, BookOpen, Sparkles } from "lucide-react"
import { useCallback, useMemo, useState } from "react"
import { useDebouncedCallback } from "use-debounce"
import camelcaseKeys from "camelcase-keys"

import { MarketingNav } from "@/components/marketing-nav"
import { MarketingFooter } from "@/components/marketing-footer"
import { Pagination } from "@/components/pagination"
import { Input } from "@/components/ui/input"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { usePrefetchLink } from "@/hooks/use-prefetch-link"
import { cn } from "@/lib/utils"
import type { Phrase, PhraseFilters } from "@/types/phrase"

interface PaginationData {
  currentPage: number
  nextPage: number | null
  prevPage: number | null
  totalPages: number
}

interface Props {
  phrases: Phrase[]
  totalCount: number
  filters?: PhraseFilters
  pagination?: PaginationData
  canonicalUrl?: string
}

export default function PhrasesIndex(props: unknown) {
  const page = usePage()
  const { auth } = page.props

  const data = useMemo(
    () => camelcaseKeys(props as Record<string, unknown>, { deep: true }) as unknown as Props,
    [props]
  )

  const { phrases, totalCount, filters, pagination, canonicalUrl } = data
  const [search, setSearch] = useState(filters?.search ?? "")

  // Prefetch links
  const dashboardPrefetch = usePrefetchLink("/dashboard")
  const signUpPrefetch = usePrefetchLink("/sign_up")
  const aboutPrefetch = usePrefetchLink("/about")

  // Debounced search
  const debouncedSearch = useDebouncedCallback((value: string) => {
    const params: Record<string, string> = {}
    if (value.length >= 2) params.q = value
    router.visit("/phrases", {
      data: params,
      preserveState: true,
      preserveScroll: true,
    })
  }, 200)

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setSearch(value)
      debouncedSearch(value)
    },
    [debouncedSearch]
  )

  const clearSearch = useCallback(() => {
    setSearch("")
    router.visit("/phrases")
  }, [])

  // SEO: Breadcrumbs Structured Data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://thaicopilot.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Phrases",
        item: "https://thaicopilot.com/phrases",
      },
    ],
  }

  const pageTitle = pagination && pagination.currentPage > 1
    ? `Thai Phrases (Page ${pagination.currentPage}) - Learn Common Expressions`
    : "Thai Phrases - Learn How to Say Common Phrases in Thai"

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content="Browse our collection of Thai phrases with audio pronunciation, romanization, and translations. Learn how to say common phrases in Thai."
        />
        <meta name="robots" content="index, follow" />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {pagination?.prevPage && (
          <link rel="prev" href={`/phrases?page=${pagination.prevPage}`} />
        )}
        {pagination?.nextPage && (
          <link rel="next" href={`/phrases?page=${pagination.nextPage}`} />
        )}

        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta
          property="og:description"
          content="Browse our collection of Thai phrases with audio pronunciation, romanization, and translations."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl || "https://thaicopilot.com/phrases"} />
        <meta property="og:image" content="https://thaicopilot.com/icon.png" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content="Browse our collection of Thai phrases with audio pronunciation, romanization, and translations." />
        <meta name="twitter:image" content="https://thaicopilot.com/icon.png" />

        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Head>

      <MarketingNav />

      {/* Hero Section - refined gradient matching dashboard */}
      <div className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-br from-slate-50 via-white to-indigo-50/50 dark:border-slate-800 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950/30">
        {/* Decorative background elements */}
        <div className="pointer-events-none absolute -top-24 -right-24 size-72 rounded-full bg-indigo-500/5 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 size-72 rounded-full bg-violet-500/5 blur-3xl" />

        <div className="relative mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-8 flex items-center gap-2 text-sm text-muted-foreground">
            <Link
              href="/"
              className="transition-colors hover:text-indigo-600 dark:hover:text-indigo-400"
            >
              Home
            </Link>
            <span className="text-slate-300 dark:text-slate-600">/</span>
            <span className="font-medium text-foreground">Phrases</span>
          </nav>

          <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-4 flex items-center gap-4">
                <div className="flex size-14 items-center justify-center rounded-2xl bg-indigo-100 dark:bg-indigo-900/50">
                  <BookOpen className="size-7 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                    Thai Phrases
                  </h1>
                  <p className="mt-1 text-muted-foreground">
                    Essential expressions with audio
                  </p>
                </div>
              </div>
              <p className="text-lg leading-relaxed text-slate-600 dark:text-slate-400">
                Master common Thai phrases with native audio pronunciation,
                romanization guides, and contextual translations.
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="mt-8 max-w-xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-slate-400" />
              <Input
                type="text"
                placeholder="Search phrases... (e.g., 'hello', 'thank you')"
                value={search}
                onChange={handleSearchChange}
                className="h-12 border-slate-200 bg-white pl-12 pr-4 text-base shadow-sm transition-shadow focus:shadow-md dark:border-slate-700 dark:bg-slate-800"
              />
              {search && (
                <button
                  onClick={clearSearch}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-500 transition-colors hover:text-slate-700 dark:hover:text-slate-300"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Phrases List */}
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
          {/* Results count */}
          <div className="mb-6 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {search ? (
                <>
                  Found <span className="font-semibold text-foreground">{phrases.length}</span>{" "}
                  phrase{phrases.length !== 1 ? "s" : ""} matching "{search}"
                </>
              ) : (
                <>
                  Showing <span className="font-semibold text-foreground">{phrases.length}</span> of{" "}
                  <span className="font-semibold text-foreground">{totalCount}</span> phrases
                </>
              )}
            </p>
          </div>

          {/* Phrases Grid */}
          {phrases.length === 0 ? (
            <Card className="border-dashed border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="mb-4 rounded-full bg-slate-100 p-4 dark:bg-slate-800">
                  <Search className="size-8 text-slate-400" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-foreground">
                  No phrases found
                </h3>
                <p className="mb-6 max-w-sm text-center text-sm text-muted-foreground">
                  {search
                    ? `No phrases match "${search}". Try a different search term.`
                    : "Check back soon for new phrases!"}
                </p>
                {search && (
                  <Button variant="outline" onClick={clearSearch}>
                    Clear Search
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {phrases.map((phrase, index) => (
                <Link
                  key={phrase.id}
                  href={`/phrases/${phrase.slug}`}
                  className="group"
                >
                  <Card
                    className={cn(
                      "relative h-full overflow-hidden bg-white transition-all duration-300",
                      "hover:-translate-y-1 hover:shadow-lg hover:shadow-indigo-500/10",
                      "border-slate-200 hover:border-indigo-300",
                      "dark:border-slate-700 dark:bg-slate-900 dark:hover:border-indigo-700",
                      "animate-in fade-in slide-in-from-bottom-4 fill-mode-both"
                    )}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {/* Subtle gradient overlay on hover */}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-transparent to-indigo-500/[0.02] opacity-0 transition-opacity group-hover:opacity-100" />

                    <CardContent className="relative p-6">
                      {/* English phrase - changed to h2 for semantic structure */}
                      <h2 className="mb-3 text-lg font-semibold text-foreground transition-colors group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                        {phrase.content}
                      </h2>

                      {/* Romanization - hero (what learners focus on) */}
                      {phrase.romanization && (
                        <p className="mb-2 text-2xl font-medium text-slate-800 dark:text-slate-200">
                          {phrase.romanization}
                        </p>
                      )}

                      {/* Thai text - secondary */}
                      <p className="mb-4 text-base text-slate-500 dark:text-slate-400">
                        {phrase.thaiText}
                      </p>

                      {/* Footer */}
                      <div className="flex items-center justify-between border-t border-slate-100 pt-4 dark:border-slate-800">
                        <div className="flex items-center gap-2">
                          {phrase.audioUrl && (
                            <Badge variant="secondary" className="gap-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                              <Volume2 className="size-3" />
                              Audio
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-xs font-medium text-indigo-600 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100 dark:text-indigo-400">
                          <span>Learn</span>
                          <ArrowRight className="size-3.5" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination && phrases.length > 0 && (
            <div className="mt-12">
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                prevPage={pagination.prevPage}
                nextPage={pagination.nextPage}
                queryParams={(filters as unknown as Record<string, string>) || {}}
              />
            </div>
          )}

          {/* CTA Section */}
          {phrases.length > 0 && (
            <div className="relative mt-16 overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-indigo-50/50 p-8 text-center dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950/30 sm:p-12">
              {/* Decorative elements */}
              <div className="pointer-events-none absolute -top-12 -right-12 size-48 rounded-full bg-indigo-500/10 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-12 -left-12 size-48 rounded-full bg-violet-500/10 blur-3xl" />

              <div className="relative">
                <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-2xl bg-indigo-100 dark:bg-indigo-900/50">
                  <Sparkles className="size-8 text-indigo-600 dark:text-indigo-400" />
                </div>
                <h2 className="mb-4 text-2xl font-bold text-foreground sm:text-3xl">
                  Ready to learn Thai faster?
                </h2>
                <p className="mx-auto mb-8 max-w-xl text-slate-600 dark:text-slate-400">
                  Practice with AI-powered conversations and build lasting memory
                  with spaced repetition flashcards.
                </p>
                <div className="flex flex-col justify-center gap-4 sm:flex-row">
                  <Button asChild size="lg" className="gap-2 shadow-lg shadow-indigo-500/20">
                    <Link
                      href={auth.user ? "/dashboard" : "/sign_up"}
                      {...(auth.user ? dashboardPrefetch : signUpPrefetch)}
                    >
                      <Sparkles className="size-5" />
                      {auth.user ? "Go to Dashboard" : "Get Started Free"}
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link href="/about" {...aboutPrefetch}>
                      Learn More
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <MarketingFooter />
    </>
  )
}
